import {Route} from "~services/RoutesService";
import React, {Component} from "react";
import cx from 'classnames';
import styles from "./styles.module.scss";
import i18next from 'i18next';
import {Project} from "~screens/Dashboard/screens/Projects/types";
import ContentType from "~components/ContentType";
import DottedProgress from "~components/DottedProgress";
import ReactTooltip from 'react-tooltip';
import OldBreadcrumb from "~components/OldBreadcrumb";
import {flagResolver} from "~utils/flagResolver";
import PublicationBadge from "~components/PublicationBadge";

type OnHoverFunction = (id: number | string) => void;
type OnLeaveFunction = () => void;
type OnGotoFunction = (id: number | string) => void;

interface MapListProps {
    elements: (Route | Project)[];
    hoveredId?: number | string;
    onHover?: OnHoverFunction;
    onLeave?: OnLeaveFunction;
    onGoto?: OnGotoFunction;
    listTitle?: string;
    loading?: boolean;
}

const PLACEHOLDER_ITEMS = 4;

const MapListPlaceholder = () => (
    <div aria-busy="true" aria-label={i18next.t('map:loading') as string}>
        {Array.from({length: PLACEHOLDER_ITEMS}, (_, index) => (
            <React.Fragment key={`map-list-placeholder-${index}`}>
                <div className={cx(styles.route, styles.skeletonItem)}>
                    <div className={cx(styles.skeleton, styles.skeletonFlag)}/>
                    <div className={cx(styles.skeleton, styles.skeletonLine)}/>
                    <div className={cx(styles.skeleton, styles.skeletonLine, styles.skeletonLineShort)}/>
                    <div className={cx(styles.skeleton, styles.skeletonChip)}/>
                </div>
                <div className={styles.routeHorizontalSeparator}></div>
            </React.Fragment>
        ))}
    </div>
);

export class ElementHelper {
    static isProject(element: Route | Project): element is Project {
        return (element as Project).route !== undefined;
    }

    static isRoute(element: Route | Project): element is Route {
        return (element as Route).countriesFormatted !== undefined;
    }

    static getElementId(element: Route | Project) {
        if (this.isProject(element)) {
            return element.code;
        }
        if (this.isRoute(element)) {
            return element.id;
        }
        return undefined;
    }

    static getCountries(element: Route | Project) {
        if (this.isProject(element)) {
            return element.countries.split(',');
        }
        if (this.isRoute(element)) {
            return element.countriesFormatted.split(', ');
        }
        return [];
    }

    static getTotalInvestmentFormatted(element: Route | Project) {
        return element.totalInvestmentFormatted;

    }

    static getName(element: Route | Project) {
        return element.name;
    }

    static getProjects(element: Route | Project) {
        if (this.isProject(element)) {
            return 0;
        }
        if (this.isRoute(element)) {
            return element.projects;
        }
        return 0;

    }
}

const MapElementInfo = ({element}: { element: Route | Project }) => {
    const isProject = ElementHelper.isProject(element);

    if (isProject) {
        return <DottedProgress order={element.iirsaStage.order} className="full-width" />
    }

    return (
        <React.Fragment>
            <div className={styles.routeInfoItem}>
                <div className={styles.routeInfoItemLabel}>{i18next.t('map:projects')}</div>
                <div className={styles.routeInfoProjectsItemValue}>{ElementHelper.getProjects(element)}</div>
            </div>
            <div className={styles.routeInfoItem}>
                <div className={styles.routeInfoItemLabel}>{i18next.t('map:investmentUsd')}</div>
                <div
                    className={styles.routeInfoItemValue}>{ElementHelper.getTotalInvestmentFormatted(element)}</div>
            </div>
        </React.Fragment>
    );
}


class MapListItemComponent extends Component<{
    element: Route | Project,
    hoveredId?: number | string,
    onHover?: OnHoverFunction,
    onLeave?: OnLeaveFunction,
    onGoto?: OnGotoFunction
}, {}> {
    SPANISH_TO_ENGLISH_INFRAESTRUCTURES = {
        ['transporte']: 'transport',
        ['energía']: 'energy',
        ['fronteras']: 'boundary',
        ['comunicaciones']: 'telecommunications',
    };

    toKey = (spanish: string) => {
        return this.SPANISH_TO_ENGLISH_INFRAESTRUCTURES[spanish.toLowerCase()];
    }

    handleOnRouteClick = () => {

    }

    unsubscribeFlags?: () => void;

    componentDidMount() {
        this.unsubscribeFlags = flagResolver.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount() {
        if (this.unsubscribeFlags) this.unsubscribeFlags();
    }

    renderFlags(route: Route | Project) {
        const countries = ElementHelper.getCountries(route);
        const flags = countries.map(country => flagResolver.resolve(country));

        return flags.map((flag, index) => {
            if (!flag) return null;
            return (
                <>
                    <a href={'#'} data-tip={countries[index]}>
                        <img key={`flag-${index}`} src={flag} alt={countries[index]} className={styles.flag}/>
                    </a>
                    <ReactTooltip/>
                </>
            );
        });
    }

    gotoElem(element: Route | Project) {
        const id = ElementHelper.getElementId(element);
        if (!id) {
            console.error('Element has no id', element)
            return;
        }
        this.props?.onGoto?.(id);
    }

    mouseOverElement = (element: Route | Project) => {
        const id = ElementHelper.getElementId(element);
        if (!id) {
            console.error('Element has no id', element)
            return;
        }
        this.props?.onHover?.(id);
    }

    renderContentType(element: Route | Project) {
        if (ElementHelper.isProject(element)) {
            return (
                <div className={styles.projectHeader}>
                    <ContentType infrastructure={element.iirsaSector} subsector={element.iirsaSubsector} compact dark/>
                </div>
            );
        }
        return <></>
    }

    renderHeader(element: Route | Project) {
        if (ElementHelper.isProject(element)) {
            return (
                <div className={styles.projectHeader}>
                    <div className={styles.routeFlag}>
                        {this.renderFlags(element)}
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.routeFlag}>
            {this.renderFlags(element)}
            </div>
        );

    }

    render() {
        const {element, onHover, onLeave} = this.props;
        const isProject = ElementHelper.isProject(element);
        return (
            <>
            <div onClick={(e) => this.gotoElem(element)} key={`route-link-${ElementHelper.getElementId(element)}`}
                     className={styles.routeLink}>
                    <div className={styles.route} onMouseOver={e => this.mouseOverElement(element)}
                         onMouseOut={e => onLeave?.()}>
                        {this.renderHeader(element)}
                        <div className={styles.routeName}>{ElementHelper.getName(element)}</div>
                        {isProject && <PublicationBadge status={(element as Project).publicationStatus}/>}
                        {this.renderContentType(element)}
                        <div className={isProject ? styles.routeInfo : styles.routeInfoRoute }>
                            <MapElementInfo element={element}/>
                        </div>
                    </div>
                </div>
                <div className={styles.routeHorizontalSeparator}></div>
            </>
        );
    }
}

class MapListComponent extends Component<MapListProps, {}> {
    render() {
        const {elements, hoveredId, onHover, onLeave, onGoto, listTitle, loading} = this.props;
        const paths: {name: string; url?: string}[] = [{name: i18next.t('map:home') as string}, {name: (i18next.t('map:maps') as string).toUpperCase()}];
        if (listTitle) {
            paths.push({name: listTitle})
        }
        return (
            <div>
                <div className={styles.routeListHeading}>
                    <OldBreadcrumb paths={paths} className={styles.breadcrumb}/>
                    {loading ? (
                        <div className={cx(styles.skeleton, styles.skeletonTitle)}/>
                    ) : (
                        <span>
                            {listTitle || i18next.t('map:maps')}
                        </span>
                    )}
                </div>
                {loading && <MapListPlaceholder/>}
                {!loading && elements?.map(element => (
                    <MapListItemComponent key={`map-list-elem-${ElementHelper.getElementId(element)}`} element={element}
                                          hoveredId={hoveredId} onHover={onHover}
                                          onLeave={onLeave} onGoto={onGoto}/>
                ))}
            </div>
        );
    }
}

export const MapList = MapListComponent;